import React from 'react';
import dom from 'react-dom/client';

import Router, {Link, Redirect, RouterConfig, RouterView} from "./router";
import KeycloakApi from "../lib/keycloak";
import Overview from "./overview";
import Kickoff, {Done} from "./kickoff/kickoff";
import OAuthCallback from "./callback";
import StaffOnly from "./staff-only";
import Avatar, {useUser} from "./components/user";
import WireframeEditor from "./editor/wireframe-editor";
import WireframePicker, {WireframeFetch} from "./wireframe-picker";
import N8nApi from "../lib/n8n";

export interface Config {
	"oidcDiscovery": string,
	"clientId": string,
	"kickoffUrl": string,
	"n8n": {
		"baseUrl": string,
		"kickoff-webhook": string,
		"save-wireframe-webhook": string,
		"customerTableId": string,
	}
}

const config: Config = await fetch("/settings.conf")
	.then((res) => res.json());

export {
	config
};

export const keycloak = await KeycloakApi.fromOidcDiscovery(new URL(config.oidcDiscovery))
	.catch(err => {
		console.error(err);
		return new class extends KeycloakApi {
			constructor() {
				super('', {
					authorization_endpoint: "",
					issuer: "",
					jwks_uri: "",
					token_endpoint: "",
					userinfo_endpoint: ""
				})
			}
		}
	});

export const n8nApi = new N8nApi(new URL(config.n8n.baseUrl), keycloak);
export const N8N = React.createContext(n8nApi);

const routes: RouterConfig = {
	'/': () => <StaffOnly>
		<Overview/>
	</StaffOnly>,
	'/callback': () => <OAuthCallback/>,
	'/kickoff': () => <StaffOnly>
		<Kickoff keycloak={keycloak}/>
	</StaffOnly>,
	'/kickoff/done': () => <StaffOnly>
		<Done/>
	</StaffOnly>,
	'/wireframe/:project/:variant': props => <StaffOnly>{
		props.state ?
			<WireframeEditor wireframe={props.state}/> :
			<Redirect to={`/wireframe/fetch/${props.match.pathname.groups['project']}/${props.match.pathname.groups['variant']}`} />
	}</StaffOnly>,
	'/wireframe/upload': props => <StaffOnly>{
		props.state ?
			<WireframeEditor wireframe={props.state}/> :
			<Redirect to={`/wireframe`} />
	}</StaffOnly>,
	'/wireframe/fetch/:project/:variant': props => <StaffOnly>
		<WireframeFetch project={props.match.pathname.groups['project']!} variant={props.match.pathname.groups['variant']!} />
	</StaffOnly>,
	'/wireframe': () => <StaffOnly>
		<WireframePicker/>
	</StaffOnly>
};

export default function Main() {
	const user = useUser(keycloak);

	return <Router config={routes}>
		<header id="header">
			<div id="logo">
				<Link to="/">
					<picture>
						<source srcSet="https://akroservices.de/img/logo/logo-horizontal-512.png"
						        media="prefers-color-scheme: light"/>
						<source srcSet="https://akroservices.de/img/logo/logo-horizontal-512.png"
						        media="prefers-color-scheme: dark"/>
						<img src="https://akroservices.de/img/logo/logo-horizontal-512.png" alt="Akrobus Logo"/>
					</picture>
				</Link>
			</div>

			<div id="centre-nav">
				<nav>
					<Link className='button' to="/">{"Übersicht"}</Link>
					<Link className='button' to="/kickoff">{"Kickoff"}</Link>
					<Link className='button' to="/wireframe">{"Wireframe Editor"}</Link>
				</nav>
			</div>

			<nav id="app-links">
				{user ? <Avatar user={user}/> : <button onClick={() => keycloak.login()}>Anmelden</button>}
			</nav>
		</header>

		<RouterView/>
	</Router>;
}

const root = document.querySelector("#root")
	?? document.body.appendChild(document.createElement('section'));

const render = dom.createRoot(root);

render.render(<Main/>);
